"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.dueBeforeBound = exports.openOccurrenceIndexKeys = exports.ENTITY_PATIENT_PK = exports.patientIndexKeys = exports.activeMedIndexPk = exports.activeMedIndexKeys = exports.careLinkIndexPrefix = exports.careLinkIndexKeys = exports.inviteKey = exports.digestLockKey = exports.notificationKey = exports.pushSubscriptionKey = exports.occurrenceDayPrefix = exports.occurrenceKey = exports.occurrenceSk = exports.eventRangeBounds = exports.eventDayPrefix = exports.eventKey = exports.eventSk = exports.medVersionKey = exports.medVersionSk = exports.careLinkKey = exports.patientProfileKey = exports.userProfileKey = exports.patientPk = exports.userPk = exports.OCC_OPEN = exports.GSI2 = exports.GSI1 = exports.SK = void 0;
/** Sort-key prefixes. Ordering between them matters — see EVT/MED/OCC note below. */
exports.SK = {
    PROFILE: 'PROFILE',
    CAREGIVER: 'CAREGIVER#',
    /** Note: `MED#` sorts between `EVT#` and `OCC#`, so a day's events and its scheduled
     *  slots cannot be fetched in a single range query. Two queries at ~0.5 RCU each is
     *  cheaper than contorting the key space to merge them. */
    MED: 'MED#',
    EVENT: 'EVT#',
    OCCURRENCE: 'OCC#',
    PUSH_SUB: 'PUSHSUB#',
    NOTIFICATION: 'NOTIF#',
    DIGEST_LOCK: 'DIGESTLOCK#',
    INVITE: 'INVITE',
};
exports.GSI1 = 'gsi1';
exports.GSI2 = 'gsi2';
/** Sparse GSI2 partition holding every occurrence still awaiting a reminder or a miss. */
exports.OCC_OPEN = 'OCC_OPEN';
const MED_VERSION_WIDTH = 10;
// ---------------------------------------------------------------------------
// Partition keys
// ---------------------------------------------------------------------------
const userPk = (userId) => `USER#${userId}`;
exports.userPk = userPk;
const patientPk = (patientId) => `PATIENT#${patientId}`;
exports.patientPk = patientPk;
// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------
const userProfileKey = (userId) => ({
    pk: (0, exports.userPk)(userId),
    sk: exports.SK.PROFILE,
});
exports.userProfileKey = userProfileKey;
const patientProfileKey = (patientId) => ({
    pk: (0, exports.patientPk)(patientId),
    sk: exports.SK.PROFILE,
});
exports.patientProfileKey = patientProfileKey;
const careLinkKey = (patientId, caregiverUserId) => ({
    pk: (0, exports.patientPk)(patientId),
    sk: `${exports.SK.CAREGIVER}${(0, exports.userPk)(caregiverUserId)}`,
});
exports.careLinkKey = careLinkKey;
/**
 * Medication versions are immutable and zero-padded so lexical order equals numeric
 * order. History is never rewritten; every dose event pins the version it was taken
 * under, so changing the regimen cannot retroactively alter what the chart says.
 */
const medVersionSk = (medId, version) => {
    if (!Number.isInteger(version) || version < 1) {
        throw new RangeError(`Medication version must be a positive integer, got ${String(version)}`);
    }
    return `${exports.SK.MED}${medId}#V#${String(version).padStart(MED_VERSION_WIDTH, '0')}`;
};
exports.medVersionSk = medVersionSk;
const medVersionKey = (patientId, medId, version) => ({
    pk: (0, exports.patientPk)(patientId),
    sk: (0, exports.medVersionSk)(medId, version),
});
exports.medVersionKey = medVersionKey;
/**
 * Event sort key: `EVT#<logicalDate>#<takenAtUtc>#<eventId>`.
 *
 * - `logicalDate` first, so one begins_with() returns exactly one chart row.
 * - `takenAtUtc` second, so events come back already in clinical order — including
 *   the two distinct instants inside a repeated DST hour.
 * - `eventId` last (a client-generated UUIDv7), so the key is deterministic from the
 *   payload. A replayed offline sync rebuilds the identical key and the conditional
 *   write no-ops instead of creating a duplicate dose.
 */
const eventSk = (logicalDate, takenAtUtc, eventId) => `${exports.SK.EVENT}${logicalDate}#${takenAtUtc}#${eventId}`;
exports.eventSk = eventSk;
const eventKey = (patientId, logicalDate, takenAtUtc, eventId) => ({
    pk: (0, exports.patientPk)(patientId),
    sk: (0, exports.eventSk)(logicalDate, takenAtUtc, eventId),
});
exports.eventKey = eventKey;
/** Every event on one chart row. */
const eventDayPrefix = (logicalDate) => `${exports.SK.EVENT}${logicalDate}#`;
exports.eventDayPrefix = eventDayPrefix;
/** Inclusive range bounds for a multi-day report query. */
const eventRangeBounds = (from, to) => ({
    start: `${exports.SK.EVENT}${from}#`,
    end: `${exports.SK.EVENT}${to}#￿`,
});
exports.eventRangeBounds = eventRangeBounds;
/**
 * One occurrence per scheduled slot per day. Deterministic, so materialization can
 * run every five minutes with a conditional put and stay idempotent.
 */
const occurrenceSk = (logicalDate, medId, slotId) => `${exports.SK.OCCURRENCE}${logicalDate}#${medId}#${slotId}`;
exports.occurrenceSk = occurrenceSk;
const occurrenceKey = (patientId, logicalDate, medId, slotId) => ({
    pk: (0, exports.patientPk)(patientId),
    sk: (0, exports.occurrenceSk)(logicalDate, medId, slotId),
});
exports.occurrenceKey = occurrenceKey;
const occurrenceDayPrefix = (logicalDate) => `${exports.SK.OCCURRENCE}${logicalDate}#`;
exports.occurrenceDayPrefix = occurrenceDayPrefix;
const pushSubscriptionKey = (userId, endpointHash) => ({
    pk: (0, exports.userPk)(userId),
    sk: `${exports.SK.PUSH_SUB}${endpointHash}`,
});
exports.pushSubscriptionKey = pushSubscriptionKey;
const notificationKey = (userId, sentAtUtc, notificationId) => ({
    pk: (0, exports.userPk)(userId),
    sk: `${exports.SK.NOTIFICATION}${sentAtUtc}#${notificationId}`,
});
exports.notificationKey = notificationKey;
/** Conditional-put lock that makes the weekly digest exactly-once per user per week. */
const digestLockKey = (userId, isoWeek) => ({
    pk: (0, exports.userPk)(userId),
    sk: `${exports.SK.DIGEST_LOCK}${isoWeek}`,
});
exports.digestLockKey = digestLockKey;
/** Invites are keyed by token hash, so the raw token never touches the database. */
const inviteKey = (tokenHash) => ({
    pk: `INVITE#${tokenHash}`,
    sk: exports.SK.INVITE,
});
exports.inviteKey = inviteKey;
// ---------------------------------------------------------------------------
// Index keys
// ---------------------------------------------------------------------------
/** GSI1: every patient a caregiver can reach, in one query. */
const careLinkIndexKeys = (caregiverUserId, patientId) => ({
    gsi1pk: (0, exports.userPk)(caregiverUserId),
    gsi1sk: `CARELINK#${patientId}`,
});
exports.careLinkIndexKeys = careLinkIndexKeys;
exports.careLinkIndexPrefix = 'CARELINK#';
/**
 * GSI1, sparse: only the currently-effective version of each medication carries these.
 * Superseding a regimen REMOVEs gsi1pk from the old version, which drops it out of the
 * index — so "the active regimen" is a query, never a client-side max(version).
 */
const activeMedIndexKeys = (patientId, medId) => ({
    gsi1pk: `${(0, exports.patientPk)(patientId)}#MED_ACTIVE`,
    gsi1sk: `${exports.SK.MED}${medId}`,
});
exports.activeMedIndexKeys = activeMedIndexKeys;
const activeMedIndexPk = (patientId) => `${(0, exports.patientPk)(patientId)}#MED_ACTIVE`;
exports.activeMedIndexPk = activeMedIndexPk;
const patientIndexKeys = (patientId) => ({
    gsi1pk: 'ENTITY#PATIENT',
    gsi1sk: `ACTIVE#${patientId}`,
});
exports.patientIndexKeys = patientIndexKeys;
exports.ENTITY_PATIENT_PK = 'ENTITY#PATIENT';
/**
 * GSI2, sparse: the scheduler's work queue, ordered by due time. Taking a dose REMOVEs
 * gsi2pk in the same transaction that writes the event, so a dose can never be both
 * recorded and reported missed.
 */
const openOccurrenceIndexKeys = (dueAtUtc, patientId, medId, slotId) => ({
    gsi2pk: exports.OCC_OPEN,
    gsi2sk: `${dueAtUtc}#${patientId}#${medId}#${slotId}`,
});
exports.openOccurrenceIndexKeys = openOccurrenceIndexKeys;
/** Upper bound for "everything due at or before now". */
const dueBeforeBound = (nowUtcIso) => `${nowUtcIso}#￿`;
exports.dueBeforeBound = dueBeforeBound;
//# sourceMappingURL=keys.js.map